// buildSheet — the framework-agnostic Sheet pipeline: fetch a Clip's bytes from
// its presigned R2 URL, decode the whole clip, and render the contact-Sheet PNG.
// This is the orchestration the UI calls; it stays free of React so the same flow
// could back a SvelteKit shell (ADR-0021). The pure sampling lives in sheet.ts
// (unit-tested); this glues fetch → decodeGif → renderContactSheet.

import { decodeGif, type GifFrame } from "./gifDecode";
import {
  renderContactSheet,
  type RenderSheetOptions,
} from "./sheet";

/** A Clip is ≤ CLIP_MAX_FRAMES = 50; decode the whole clip, then sample it down. */
const CLIP_MAX_FRAMES = 50;

/** Injectable seam so the flow is testable without a real network. */
export interface BuildSheetDeps {
  fetch?: typeof fetch;
}

/**
 * Fetch + decode a Clip's frames — the slow, ONCE-per-clip half. Re-rendering the
 * sheet from these frames (renderContactSheet) is cheap and needs no re-fetch, so the
 * overlay decodes once on open and re-renders on each layout/look toggle instead of
 * re-hitting the short-TTL URL every time (which 403'd on a left-open overlay; #138).
 */
export async function decodeClipFromUrl(
  url: string,
  deps: BuildSheetDeps = {},
): Promise<GifFrame[]> {
  const doFetch = deps.fetch ?? globalThis.fetch.bind(globalThis);
  const res = await doFetch(url);
  if (!res.ok) throw new Error(`fetch failed (${res.status})`);
  const buffer = await res.arrayBuffer();
  const frames = decodeGif(buffer, CLIP_MAX_FRAMES);
  if (frames.length === 0) throw new Error("could not read this clip");
  return frames;
}

/** The split decode/render seam the ContactSheet overlay drives (injectable in tests). */
export interface SheetCores {
  /** Fetch + decode the clip once (the expensive half). */
  decodeClip: (url: string) => Promise<GifFrame[]>;
  /** Render the contact-sheet PNG from already-decoded frames (cheap; per toggle). */
  renderSheet: (
    frames: readonly GifFrame[],
    options: RenderSheetOptions,
  ) => Promise<Blob>;
}

/** The real cores wiring — decode over the network, render via canvas. */
export const defaultSheetCores: SheetCores = {
  decodeClip: (url) => decodeClipFromUrl(url),
  renderSheet: (frames, options) => renderContactSheet(frames, options),
};

/**
 * Fetch a Clip from `url`, decode it, and render a contact-Sheet PNG Blob in one shot —
 * the convenience composition of decodeClipFromUrl + renderContactSheet, kept for a
 * non-React consumer (e.g. a SvelteKit shell, ADR-0021). The ContactSheet overlay does NOT
 * use this: it splits decode (once) from render (per toggle) via the SheetCores seam (#138).
 */
export async function buildSheetFromUrl(
  url: string,
  options: RenderSheetOptions = {},
  deps: BuildSheetDeps = {},
): Promise<Blob> {
  const frames = await decodeClipFromUrl(url, deps);
  return renderContactSheet(frames, options);
}
